import { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { bannersAPI, getImageUrl } from '../../lib/api';
import { useApiQuery } from '../../hooks/useApiQuery';
import { Plus, Pencil, Trash2, ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorHandler';

const AdminBanners = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [formData, setFormData] = useState({
    title: '',
    redirect_url: '',
  });

  const { data: bannersRes, isLoading: loading, refetch: loadBanners } = useApiQuery(
    ['banners'],
    () => bannersAPI.getAll()
  );

  const banners = bannersRes?.data || [];

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      
      // Create preview URL
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      
      // Get image dimensions
      const img = new Image();
      img.onload = () => {
        setImageDimensions({ width: img.width, height: img.height });
      };
      img.src = url;
    }
  };

  const resetForm = () => {
    setFile(null);
    setPreviewUrl('');
    setImageDimensions({ width: 0, height: 0 });
    setFormData({ title: '', redirect_url: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select an image');
      return;
    }
    try {
      await bannersAPI.create(file, formData.title, formData.redirect_url);
      toast.success('Banner created successfully');
      setShowDialog(false);
      resetForm();
      loadBanners();
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to create banner');
    }
  };

  const handleToggleActive = async (id, isActive) => {
    try {
      await bannersAPI.update(id, { is_active: !isActive });
      toast.success('Banner updated');
      loadBanners();
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to update banner');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;
    try {
      await bannersAPI.delete(id);
      toast.success('Banner deleted');
      loadBanners();
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to delete banner');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-banners-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl md:text-3xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Banners
            </h1>
            <p className="text-slate-600">Manage homepage banner slider</p>
          </div>
          <Dialog open={showDialog} onOpenChange={(open) => {
            setShowDialog(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-[#8acb95] text-white hover:bg-[#7bb885]" data-testid="add-banner-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Banner
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add New Banner</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="banner-form">
                <div>
                  <label className="block text-sm font-medium mb-2">Banner Image *</label>
                  <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800 font-medium">📐 Recommended Dimensions:</p>
                    <p className="text-sm text-blue-700">• Desktop: 1920 × 600 px (16:5 ratio)</p>
                    <p className="text-sm text-blue-700">• Mobile: Will auto-scale to fit</p>
                    <p className="text-xs text-blue-600 mt-1">Use high-quality JPG or PNG images for best results</p>
                  </div>
                  <Input
                    type="file"
                    accept="image/*"
                    required
                    onChange={handleFileChange}
                    data-testid="banner-image-input"
                  />
                  {previewUrl && (
                    <div className="mt-4 space-y-2">
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-4 text-sm">
                          <span className="font-medium text-slate-700">Your Image:</span>
                          <span className="font-semibold text-[#8acb95]">{imageDimensions.width} × {imageDimensions.height} px</span>
                        </div>
                        {(imageDimensions.width === 1920 && imageDimensions.height === 600) ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">✓ Perfect Size</span>
                        ) : (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">⚠ Different Size</span>
                        )}
                      </div>
                      <div className="border-2 border-slate-200 rounded-lg overflow-hidden">
                        <img 
                          src={previewUrl} 
                          alt="Banner preview" 
                          className="w-full h-48 object-cover"
                        />
                      </div>
                      <p className="text-xs text-slate-500 text-center">Preview of your banner image</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Title</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Banner title"
                    data-testid="banner-title-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Redirect URL</label>
                  <Input
                    value={formData.redirect_url}
                    onChange={(e) => setFormData({ ...formData, redirect_url: e.target.value })}
                    placeholder="/products"
                    data-testid="banner-url-input"
                  />
                </div>
                <Button type="submit" className="bg-[#8acb95] text-white hover:bg-[#7bb885]" data-testid="banner-submit-button">
                  Create Banner
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Banners Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            [...Array(3)].map((_, i) => (
              <div key={i} className="h-48 bg-slate-200 rounded-xl skeleton" />
            ))
          ) : banners.length === 0 ? (
            <div className="col-span-full text-center py-12 text-slate-500">
              <ImageIcon className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              No banners found
            </div>
          ) : (
            banners.map((banner) => (
              <div key={banner.id} className="bg-white rounded-xl border overflow-hidden" data-testid={`banner-${banner.id}`}>
                <img
                  src={getImageUrl(banner.image_url)}
                  alt={banner.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-semibold">{banner.title || 'Untitled'}</h3>
                      {banner.redirect_url && (
                        <p className="text-xs text-slate-500 mt-1">{banner.redirect_url}</p>
                      )}
                    </div>
                    <Badge variant={banner.is_active ? 'default' : 'secondary'}>
                      {banner.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleToggleActive(banner.id, banner.is_active)}
                      data-testid={`toggle-banner-${banner.id}`}
                    >
                      {banner.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(banner.id)}
                      data-testid={`delete-banner-${banner.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminBanners;
